/**
 * 블럭 카탈로그 → Cosmograph 그래프 데이터 변환 헬퍼.
 *
 * 엄밀히 "블럭 그래프" 는 KG 노드/엣지가 아니라 **온톨로지 구조 자체** 를 시각화한다:
 * - 노드 = 블럭 1개 (blockId 를 그래프 노드 id 로 사용)
 * - 엣지 = master shape 1개 (sourceKind → targetKind 트리플을 블럭 id 로 매핑)
 *
 * BlocksPage 의 "Master Graph" 탭이 사용. Cosmograph `GraphNode / GraphEdge` 규약을
 * 그대로 따라 기존 `KnowledgeGraph` 컴포넌트를 재사용할 수 있다.
 *
 * 설계 원칙:
 * - 순수 함수 — 입력 카탈로그에서만 파생. 네트워크/상태 의존 없음.
 * - kind → block 역참조는 "1 블럭 = 1 kind 엄격 1:1" 원칙에 기반. 다대일 매핑은 없다고 가정한다
 *   (블럭 정의 시 카탈로그 테스트가 강제).
 * - shape 의 source / target kind 가 카탈로그에 없는 경우 (레거시 데이터 등) 는 skip —
 *   Cosmograph 가 매달린 엣지로 crash 하지 않도록 필터링한다.
 */
import type { OntologyBlock, ShapeDef } from "../api/admin-client.ts"
import type { GraphEdge, GraphNode } from "../components/graph/types.ts"

/** 블럭 그래프의 입력 — 100+ 블럭 + 마스터 shape 풀. */
export interface BlocksGraphInput {
	readonly blocks: ReadonlyArray<OntologyBlock>
	readonly masterShapes: ReadonlyArray<ShapeDef>
}

/** 블럭 그래프 변환 결과 — Cosmograph 에 직결. */
export interface BlocksGraphResult {
	readonly nodes: ReadonlyArray<GraphNode>
	readonly edges: ReadonlyArray<GraphEdge>
}

/**
 * kind 이름 → 블럭 역참조 맵을 만든다.
 *
 * 1 블럭 = 1 kind 엄격 1:1 이라 매핑은 단사. 카탈로그에 없는 kind 가 shape 에서 참조되는
 * 경우는 호출 측에서 skip 하면 된다.
 */
export function buildKindToBlockMap(
	blocks: ReadonlyArray<OntologyBlock>
): ReadonlyMap<string, OntologyBlock> {
	const map = new Map<string, OntologyBlock>()
	for (const block of blocks) {
		const kind = block.slice.kinds[0]
		if (kind) map.set(kind.name, block)
	}
	return map
}

/**
 * 블럭 그래프 데이터를 만든다 — 블럭 → GraphNode, master shape → GraphEdge.
 *
 * @param input 블럭 카탈로그 + 마스터 shape 풀
 * @returns Cosmograph 에 직접 주입 가능한 `{ nodes, edges }`
 *
 * 주의: shape 의 source / target kind 가 블럭 카탈로그에 없으면 해당 엣지는 **skip** 한다.
 * 이는 데이터 정합성이 깨진 (레거시 slice 등) 입력을 안전하게 무시하기 위함.
 */
export function buildBlocksGraphData(input: BlocksGraphInput): BlocksGraphResult {
	const { blocks, masterShapes } = input
	const kindToBlock = buildKindToBlockMap(blocks)

	// 1) 블럭 → GraphNode 변환. id 는 블럭 id 를 그대로 쓴다.
	const nodes: GraphNode[] = blocks.map((block) => {
		const kind = block.slice.kinds[0]
		return {
			id: block.id,
			name: block.displayName,
			domain: block.category, // Cosmograph 그룹핑 힌트로 category 재활용
			kind: kind?.name ?? block.id,
			category: block.category
		}
	})

	// 2) master shape → GraphEdge 변환.
	//    sourceKind / targetKind 를 블럭 id 로 맵핑. kind 가 맵에 없으면 skip.
	const edges: GraphEdge[] = []
	for (const shape of masterShapes) {
		const src = kindToBlock.get(shape.sourceKind)
		const tgt = kindToBlock.get(shape.targetKind)
		if (!src || !tgt) continue
		edges.push({
			id: `${src.id}|${shape.relation}|${tgt.id}`,
			source: src.id,
			target: tgt.id,
			relation: shape.relation
		})
	}

	return { nodes, edges }
}

/**
 * 블럭 id 로 특정 그래프 노드를 찾는 유틸 — 노드 클릭 시 카드로 스크롤/모달 오픈 연동에 사용.
 *
 * Cosmograph 는 내부 인덱스로 노드를 참조하지만, BlocksPage 는 blockId 를 기준으로
 * 상호작용해야 하므로 id 기반 검색 헬퍼를 별도로 노출.
 */
export function findGraphNodeByBlockId(
	nodes: ReadonlyArray<GraphNode>,
	blockId: string
): GraphNode | undefined {
	return nodes.find((n) => n.id === blockId)
}
